// NavSearchDropdown — populates SearchBar > Dropdown with grouped global
// search results. The parent SearchBar extends DropdownParentFocus, which
// shows [dropdown] children on :focus-within via CSS.

export const NavSearchResultGroup = {
  flow: 'y',

  GroupHeader: {
    flow: 'x',
    align: 'center left',
    gap: 'Y',
    padding: 'Y1 A',

    Icon: {
      name: (el, s) => s.icon || 'search',
      boxSize: 'Z2 Z2',
      color: 'caption'
    },
    // The mono micro-label that heads every group of the redesign
    // (DESIGN_SPEC §5.7) — the launcher's section heads wear the same.
    GroupLabel: {
      extends: 'AppMicroLabel',
      // KEY-IN-STATE: `_groupResults` carries the CATEGORY_META key; an
      // unmapped server surface falls back to its own name, which polyglot
      // returns verbatim.
      text: (el, s) => el.call('polyglot', s.label || '')
    }
  },

  // RECORD rows — tickets, notes, files, people, every server surface. The
  // default: `_groupResults` stamps `kind: 'record'` on any category with no
  // explicit kind (functions/search/globalSearch.js CATEGORY_META).
  Items: {
    flow: 'y',
    // Every kind with its own slot below stays out — an ACTION group rendered
    // here too showed each chip and action twice (once as a dead record
    // link), live on dev ec09e4e87 (2026-10-07).
    if: (el, s) => s.kind !== 'apps' && s.kind !== 'commands' && s.kind !== 'actions',
    children: (el, s) => s.items || [],
    childExtends: 'NavSearchResultItem',
    childrenAs: 'state'
  },

  // APP rows — the apps group (built-in + installed apps matching the query)
  // and every zero-result suggestion group (App Store / Integrations /
  // browse). Rendered with the LAUNCHER's own row so an app reads identically
  // wherever it is offered; `flow`/`gap` mirror AppGroupColumn's GcItems, the
  // other AppListItem list in this package.
  //
  // A SECOND slot rather than a per-group `childExtends` because
  // `childExtends` is a registry key read as a raw value — never a
  // `(el, s) =>` factory — so one group component cannot pick its row
  // renderer from state any other way. `if:` (unmount), not `hide:`: a
  // group's kind is fixed for its whole life, so the unused branch is dead
  // weight, never a toggled view. Both branches are declared HERE, inside the
  // component, so neither can be clobbered by a mount-site `if:`.
  AppItems: {
    flow: 'y',
    gap: 'V',
    if: (el, s) => s.kind === 'apps',
    children: (el, s) => s.items || [],
    childExtends: 'NavSearchAppItem',
    childrenAs: 'state'
  },

  // COMMAND rows — the shell keyboard registry's rows, the ⌘K palette's
  // (DESIGN-WL-SHELL-KEYBOARD-MOTION-1; functions/search/
  // searchCommandResults.js). The third branch, declared here beside the
  // other two for the same reason: no mount-site `if:` can clobber it.
  CommandItems: {
    flow: 'y',
    if: (el, s) => s.kind === 'commands',
    children: (el, s) => s.items || [],
    childExtends: 'NavSearchCommandItem',
    childrenAs: 'state'
  },

  // ACTION rows — the ⌘K filter chips and what to do with the chipped result
  // (CMDK-05 / CMDK-08; functions/search/searchAppRecordResults.js): the
  // active chips, the values to filter by, the app hand-off, the ask, the
  // clear. The fourth branch, declared here for the same reason as the three
  // above.
  ActionItems: {
    flow: 'y',
    if: (el, s) => s.kind === 'actions',
    children: (el, s) => s.items || [],
    childExtends: 'NavSearchActionItem',
    childrenAs: 'state'
  }
}

// THE SEARCH'S TWO STATE LINES for the launcher-hosted results (9A,
// components/SearchAppsMenu.js SamResults) — the same windows and words the
// header search's own panel shows (shared HeaderSearch `Dropdown.Loading` /
// `NoResults`), so the ONE dropdown answers "still looking" and "nothing
// matched" exactly as the old results panel did.
//
// Loading — the exact window `_navSearchLoading` covers: from the keystroke
// that fired this query until ITS OWN results land.
export const NavSearchLoading = {
  subscribeTo: ['_navSearchLoading'],
  hide: (el, s) => !s.root._navSearchLoading,
  padding: 'Y1 A',
  fontSize: 'Z2',
  color: 'caption',
  role: 'status',
  text: '{{ shell.headerSearch.loading | polyglot }}'
}

// No results — once loading has settled AND the query is non-empty AND
// nothing matched (the zero-result suggestion pass usually fills the panel,
// so this shows only when even that found nothing).
export const NavSearchNoResults = {
  subscribeTo: ['_navSearchLoading', '_navSearchGrouped', 'navSearchQuery'],
  hide: (el, s) =>
    !!s.root._navSearchLoading ||
    !(s.root.navSearchQuery || '').trim() ||
    !!(s.root._navSearchGrouped && s.root._navSearchGrouped.length),
  padding: 'Y1 A',
  fontSize: 'Z2',
  color: 'caption',
  role: 'status',
  text: (el, s) =>
    el
      .call('polyglot', 'shell.headerSearch.noResults')
      .replace('{query}', (s.root.navSearchQuery || '').trim())
}

// SEARCH-1 (2026-08-14) — the keyboard-selected row. `s._flatIndex` is
// stamped onto every item by globalSearch.js's `_groupResults` (a running
// index across the WHOLE grouped set, group order then item order), so this
// stays in perfect agreement with moveNavSearchSelection's wrap arithmetic
// without either side re-deriving the flatten order.
const _isKeyboardActive = (s) =>
  typeof s._flatIndex === 'number' && s.root?._navSearchActiveIndex === s._flatIndex

export const NavSearchResultItem = {
  extends: 'Link',
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  padding: 'Y1 A',
  cursor: 'pointer',
  textDecoration: 'none',
  round: 'radiusControl',
  subscribeTo: ['_navSearchActiveIndex'],
  // House "selected" token (never overlayStrong — that's a wash, not a
  // state fill), same as every other keyboard/click-active row in this repo
  // (AppNavbar, AiMenu, CanvasViewNav, …).
  background: (el, s) => (_isKeyboardActive(s) ? 'selected' : undefined),
  // The house pointer wash (a raw `gray.05` never flipped for the dark
  // sheet) and the press step.
  ':hover': { background: 'hover' },
  ':active': { background: 'selected' },
  // A keyboard reaches this link: the inset ring the command rows of the same list wear (the row sits inside
  // a scroll box, where an outer ring is clipped) — dropdowns spec §5 / D1.
  ':focus-visible': { shadow: 'focusRingInset' },

  href: (el, s) => s._href || '#',
  // The row's words are its two child nodes (ResultTitle, ResultSubtitle),
  // so it has no text of its own, and says so. `Link` is a BARE key: it
  // resolves through the linked libraries too, and system/default's `Link`
  // carries the demo text "Link", which every result row painted before its
  // title (dev, system/hq, 2026-10-28). `text: null` cancels an inherited
  // text — the shared markdown renderer's rule (25f0dcec5).
  text: null,
  // Read back inside onContextmenu (below) instead of trusting a second,
  // later read of the reactive `s._title` — see that handler's comment.
  // Same declarative-attribute idiom HeaderTab uses for `data-tab-path`.
  attr: {
    'data-result-title': (el, s) => s._title || '',
    'aria-selected': (el, s) => String(_isKeyboardActive(s))
  },

  onClick: (e, el, s) => {
    e.preventDefault()
    el.call('navigateSearchResult', s._href)
  },

  // Right-click → Pin to navbar / Unpin (rows + dispatch in
  // functions/navSearchResultMenu.js — same house ContextDropdown contract
  // as HeaderTab/SearchAppTile's own context menus). 2026-08-02 per Nika.
  // stopPropagation so this never falls through to an ancestor's own
  // onContextmenu (none today, but matches the sibling menus' convention).
  //
  // Identity is read off the RENDERED DOM NODE (native `href` attribute +
  // the `data-result-title` companion above), NOT the reactive `s` state —
  // live-verified 2026-08-02: `s._href`/`s._title` arrived empty inside this
  // handler on a real right-click even though the SAME row's `href:`/`text:`
  // factories had rendered correctly moments earlier (globalSearch's own
  // debounced round-trip can still be in flight when a menu opens and
  // resolve afterward, rewriting the live search state the reactive `s` this
  // handler receives is bound to — see globalSearch.js's _navSearchMenuOpen
  // guard, which stops it from clobbering the ROWS but doesn't change how
  // an individual row's captured `s` behaves for this handler on the SAME
  // tick). DOM attributes were already committed by the render that put this
  // exact node under the cursor, so they can't go stale within the event —
  // `s._href`/`s._title` stay as a defensive fallback only.
  onContextmenu: (e, el, s) => {
    const node = el.node
    const path = (node && node.getAttribute('href')) || s._href
    const title = (node && node.getAttribute('data-result-title')) || s._title
    if (!path || path === '#') return
    // A title that is a bundle KEY (an app tile, a fallback label) pins as its KEY plus the resolved text, so the
    // navbar tab re-resolves on a language switch (shared navbarTabMenu.js `labelKey`, 301c03021); a title named
    // by data (a ticket, a file, a person) resolves to itself and pins as its own name, with no key.
    const resolved = el.call('polyglot', title || '')
    const labelKey = title && resolved && resolved !== title ? title : ''
    const pin = labelKey ? { path, label: resolved, labelKey } : { path, label: title }
    e.preventDefault()
    e.stopPropagation()
    s.root.update(
      {
        contextDropdown: {
          open: true,
          x: e.clientX,
          y: e.clientY,
          itemsFn: 'navSearchResultMenuItems',
          actionFn: 'navSearchResultMenuAction',
          // Read by HeaderSearch's onBlur (shared/components/HeaderSearch.js)
          // and IntranetAppHeader's own Input.onBlur to keep the search
          // dropdown open while THIS menu is up — see those files' comments.
          fromNavSearch: true,
          searchResult: pin
        }
      },
      { preventFetch: true }
    )
  },

  // The result's NAME — primary text, never cut (overflow law,
  // HOME_AMBIENT_SKIN_SPEC §3b rule 8): it wraps, a long unbroken word breaks
  // anywhere, and the subtitle keeps its place beside it.
  ResultTitle: {
    flex: '1',
    minWidth: '0',
    fontSize: 'Z2',
    fontWeight: '500',
    color: 'title',
    overflowWrap: 'anywhere',
    // KEY-IN-STATE — functions/search/globalSearch.js's row builders are
    // plain (root, ql) helpers with no element to resolve through, so their
    // FALLBACK labels travel as bundle keys and land here. A real document /
    // file / ticket name passes through polyglot untranslated.
    text: (el, s) => el.call('polyglot', s._title || '')
  },

  // Secondary meta: one line, the whole subtitle on hover (rule 8).
  ResultSubtitle: {
    fontSize: 'Y2',
    color: 'caption',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 'F',
    title: (el, s) => el.call('polyglot', s._subtitle || ''),
    text: (el, s) => el.call('polyglot', s._subtitle || ''),
    hide: (el, s) => !s._subtitle
  }
}

// An APP row inside the search results — the apps group and every
// zero-result suggestion row (App Store catalog, integration kinds, the three
// browse entry points).
//
// It EXTENDS `AppListItem`, the launcher's own wide row, rather than
// restyling a result row to look like one: the icon tile, the title/caption
// pair, the hover wash and both i18n calls are already correct there, and one
// definition means an app can never look like two different things depending
// on which surface offered it. The rows carry the launcher tile contract
// (`title` / `caption` / `icon` / `route`) alongside the search-result one —
// see functions/search/searchAppResults.js for why they are double-keyed.
//
// Two overrides, and only two:
//   • the keyboard highlight, shared with NavSearchResultItem above (same
//     `_flatIndex` stamped by `_groupResults`, so both row kinds agree with
//     moveNavSearchSelection's arithmetic without either re-deriving it);
//   • the open, which goes through `navigateSearchApp` — the launcher's
//     opener — instead of AppListItem's own `openAppPage`, so the search
//     results panel is cleared on the way out. See that function's note for
//     why an app must never be opened through `openInSlot`.
export const NavSearchAppItem = {
  extends: 'AppListItem',
  subscribeTo: ['_navSearchActiveIndex'],
  background: (el, s) => (_isKeyboardActive(s) ? 'selected' : 'transparent'),
  attr: {
    'aria-selected': (el, s) => String(_isKeyboardActive(s))
  },
  onClick: (e, el, s) => {
    e.preventDefault()
    el.call('navigateSearchApp', s.route)
  }
}

// A COMMAND row inside the search results — the ⌘K palette offers the shell
// keyboard registry's rows (HOME_AMBIENT_SKIN_SPEC §5: "The command palette
// (ws-w2) lists the same registry rows with keycaps"; matched over
// `listShortcuts` by functions/search/searchCommandResults.js).
//
// It EXTENDS the ? sheet's own row (components/ShortcutsOverlay.js
// ShortcutsSheetRow): the label through polyglot, the AppKeycap keys and the
// sheet's joiners ("G then H") come from ONE definition, so a shortcut reads
// the same in the sheet and in the palette. The row adds only what a search
// row needs:
//   • a <button> — a command is not a place: no Link, no href, no
//     pin-to-navbar menu. A pick runs it (`runSearchCommand`: the search
//     closes, then the registry runs the row);
//   • the search rows' geometry, pointer wash and press step, and the
//     keyboard highlight the other two row kinds share (`_flatIndex`);
//   • its GROUP as secondary meta ("Go to", "Dashboard") — one line, whole on
//     hover (overflow law §3b rule 8) — because "Home" alone reads like the
//     Home app;
//   • the keys, only while the keystroke works (the rows carry `keycaps` only
//     when `active`). They are visual (aria-hidden): the keystroke is named to
//     assistive technology by `aria-keyshortcuts` where one keystroke does it.
//     On the highlighted row — pointer or keyboard — they lift onto the
//     `raised` fill + `float`, the MenuPanelItem rule. Flex `order` seats them
//     after the group without moving them in the DOM, where they are hidden.
export const NavSearchCommandItem = {
  extends: 'ShortcutsSheetRow',
  tag: 'button',
  type: 'button',
  width: '100%',
  align: 'center left',
  gap: 'Z',
  padding: 'Y1 A',
  round: 'radiusControl',
  borderWidth: '0',
  color: 'title',
  textAlign: 'left',
  cursor: 'pointer',
  subscribeTo: ['_navSearchActiveIndex'],
  background: (el, s) => (_isKeyboardActive(s) ? 'selected' : 'transparent'),
  ':hover': { background: 'hover' },
  ':active': { background: 'selected' },
  // The inset ring (MenuPanelItem's reason): the results panel scrolls, and
  // an outer spread would be clipped at its edge.
  ':focus-visible': { shadow: 'focusRingInset' },
  '&:hover [data-keycap]': {
    background: 'raised',
    color: 'title',
    boxShadow: 'var(--shadow-float)'
  },
  '&[aria-selected="true"] [data-keycap]': {
    background: 'raised',
    color: 'title',
    boxShadow: 'var(--shadow-float)'
  },
  ariaKeyshortcuts: (el, s) => s.ariaKeys || null,
  attr: {
    'aria-selected': (el, s) => String(_isKeyboardActive(s))
  },

  onClick: (e, el, s) => {
    e.preventDefault()
    el.call('runSearchCommand', s._shortcutId)
  },

  // What the command does — primary text, wraps (overflow law rule 8), at
  // the search rows' size.
  ShortcutsRowLabel: {
    flex: '1',
    fontSize: 'Z2',
    overflowWrap: 'anywhere'
  },

  // Its group — secondary meta: one line, the whole of it on hover.
  CommandGroup: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 'F',
    title: (el, s) => el.call('polyglot', s.group || ''),
    text: (el, s) => el.call('polyglot', s.group || ''),
    hide: (el, s) => !s.group
  },

  ShortcutsRowKeys: {
    order: '1',
    ariaHidden: 'true'
  }
}

// An ACTION row inside the search results (CMDK-05 / CMDK-08) — a chip to
// add or remove, an app hand-off, the ask, the clear. Built by functions/
// search/searchAppRecordResults.js; a pick runs the row's `_searchAction`
// (globalSearch.js runSearchAction), the same call Enter makes on it.
//   • a <button>, like the command rows: an action is not a place — no Link,
//     no href, no pin-to-navbar menu;
//   • the search rows' geometry, pointer wash, press step and the keyboard
//     highlight every row kind shares (`_flatIndex`);
//   • its words: tenant content (an app's labels, values, names) as `_title`
//     / `_meta`, or a bundle KEY (`_titleKey` / `_metaKey`) with its
//     `{param}` values — the builders have no element to translate through;
//   • its meta on the right — a count, "48 rows", "Remove" — one line.
export const NavSearchActionItem = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  width: '100%',
  align: 'center left',
  gap: 'Z',
  padding: 'Y1 A',
  round: 'radiusControl',
  borderWidth: '0',
  color: 'title',
  textAlign: 'left',
  cursor: 'pointer',
  subscribeTo: ['_navSearchActiveIndex'],
  background: (el, s) => (_isKeyboardActive(s) ? 'selected' : 'transparent'),
  ':hover': { background: 'hover' },
  ':active': { background: 'selected' },
  // The inset ring: the results panel scrolls, and an outer spread would be
  // clipped at its edge.
  ':focus-visible': { shadow: 'focusRingInset' },
  // Which lists a value spans ("Bills 1 · Invoices 1") — only on a value that
  // spans two or more (searchAppRecordResults.js facetSpanHint): the hover
  // tooltip, and the same words as the accessible description.
  title: (el, s) => s._hint || undefined,
  attr: {
    'aria-selected': (el, s) => String(_isKeyboardActive(s)),
    'aria-description': (el, s) => s._hint || undefined
  },

  // The press must not take focus from the search field: a chip or a clear
  // keeps the palette open, and a blur ends the search 200 ms later
  // (HeaderSearch onBlur) — live on dev ec09e4e87 (2026-10-07) a real click
  // on "Customer · Pinecrest Custody" closed the palette and added no chip.
  // The launcher's own filter line does the same (SearchAppsMenu SamFilter).
  onMousedown: (e) => {
    e.preventDefault()
  },

  onClick: (e, el, s) => {
    e.preventDefault()
    el.call('runSearchAction', s._searchAction)
  },

  Icon: {
    name: (el, s) => s._icon || 'search',
    boxSize: 'Z2 Z2',
    flexShrink: '0',
    color: 'caption'
  },

  ActionLabel: {
    tag: 'span',
    flex: '1',
    minWidth: '0',
    fontSize: 'Z2',
    fontWeight: '500',
    overflowWrap: 'anywhere',
    text: (el, s) => el.call('navSearchRowText', s._title, s._titleKey, s._titleParams)
  },

  ActionMeta: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    color: 'caption',
    whiteSpace: 'nowrap',
    text: (el, s) => el.call('navSearchRowText', s._meta, s._metaKey, s._metaParams),
    hide: (el, s) => !s._meta && !s._metaKey
  }
}
